/**
 * The NISG 2026 announcement (components/ui/Announcement): which statutory
 * deadline the countdown runs to, and when a dismissal expires. Kept free of
 * browser APIs so the tests can run it against fixed clocks.
 */

/** Dismissals are stored under this localStorage key (listed in PrivacyPage). */
export const ANNOUNCEMENT_KEY = 'softmaze.announcement'
/** Bump when the announcement changes, so everyone sees the new one once. */
export const ANNOUNCEMENT_ID = 'nisg-2026'
/** A dismissed announcement stays hidden this long. */
export const ANNOUNCEMENT_SNOOZE_MS = 30 * 24 * 60 * 60 * 1000

export type MilestoneKey = 'registration' | 'declaration'

/**
 * NISG 2026 deadlines, end of day in Vienna. Registration with the
 * Bundesamt für Cybersicherheit is due three months after the law took
 * effect on 1 Oct 2026 (§ 29 Abs 3), the self-declaration of the
 * risk-management measures twelve months after that (§ 33 Abs 1).
 */
export const MILESTONES: { key: MilestoneKey; at: number }[] = [
  { key: 'registration', at: Date.UTC(2026, 11, 31, 22, 59, 59) }, // 31.12.2026 23:59:59 CET
  { key: 'declaration', at: Date.UTC(2027, 8, 30, 21, 59, 59) }, // 30.09.2027 23:59:59 CEST
]

/** The next deadline still ahead at `now`, or null once all have passed. */
export function nextMilestone(now: number): { key: MilestoneKey; at: number } | null {
  return MILESTONES.find((m) => m.at > now) ?? null
}

export type Remaining = { days: number; hours: number; minutes: number; seconds: number }

/** Whole days/hours/minutes/seconds from `now` to `at` (zeros once passed). */
export function remaining(at: number, now: number): Remaining {
  const total = Math.max(0, Math.floor((at - now) / 1000))
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  }
}

/** True when a stored dismissal (`{ id, dismissedAt }`) still hides the announcement at `now`. */
export function isDismissed(stored: string | null, now: number): boolean {
  if (!stored) return false
  try {
    const { id, dismissedAt } = JSON.parse(stored) as { id?: string; dismissedAt?: number }
    return id === ANNOUNCEMENT_ID && typeof dismissedAt === 'number' && now - dismissedAt < ANNOUNCEMENT_SNOOZE_MS
  } catch {
    return false
  }
}
